/**
 * Traduce un `MatchView` a lo que la pantalla pinta, literalmente.
 *
 * Es **puro** y no renderiza nada. Existe separado de los componentes para que
 * lo que se muestra pueda probarse sin navegador: los componentes se limitan a
 * recorrer estas estructuras.
 *
 * Las definiciones de carta se resuelven contra el catalogo, que es informacion
 * publica del juego. Eso **no** reconstruye nada oculto: el catalogo solo puede
 * resolver los `definitionId` que el servidor ha enviado, y los de la mano y el
 * mazo rival no llegan nunca.
 */

import {
  PRIMER_DECK_C5_CATALOG,
  findDefinition,
  type CardDefinition,
  type CardInstance,
  type FieldLineId,
  type FieldSlot,
  type InstanceId,
  type MatchView,
} from '@tcg/game-rules';

/** Una carta lista para pintar. */
export interface CardRow {
  readonly instanceId: InstanceId;
  readonly name: string;
  readonly type: 'Ofrenda' | 'Invocación' | 'Hechizo' | 'Desconocida';
  /** `Valor` en Ofrendas, `Coste` en las demas. */
  readonly costLabel: string;
  /** Estadisticas impresas, si el tipo las tiene. */
  readonly stats: string | null;
  readonly effectText: string | null;
  readonly description: string | null;
  /** Cierto si esta interfaz no sabe jugarla todavia. */
  readonly unsupported: boolean;
}

/** Un espacio del Campo, vacio u ocupado. */
export interface SlotRow {
  readonly line: FieldLineId;
  readonly index: 0 | 1 | 2 | 3 | 4;
  readonly occupant: OccupantRow | null;
}

export interface OccupantRow {
  readonly instanceId: InstanceId;
  readonly name: string;
  readonly kind: 'invocation' | 'spell';
  /** Solo en Invocaciones. */
  readonly power: number | null;
  readonly currentLife: number | null;
  readonly maxLife: number | null;
  readonly range: number | null;
  /** `restored` o `turned`; los Hechizos no tienen orientacion. */
  readonly orientation: string | null;
}

export interface AltarRow {
  readonly instanceId: InstanceId;
  readonly name: string;
  readonly value: number | null;
  /** `disponible` o `agotada`. */
  readonly state: string;
}

const TYPE_LABEL = {
  offer: 'Ofrenda',
  invocation: 'Invocación',
  spell: 'Hechizo',
} as const;

function definitionOf(card: CardInstance): CardDefinition | undefined {
  return findDefinition(PRIMER_DECK_C5_CATALOG, card.definitionId);
}

/**
 * Cierto si esta interfaz todavia no sabe jugar esa carta.
 *
 * Coincide a proposito con lo que el motor rechaza: Hechizos, Invocaciones con
 * efecto y Invocaciones con Penetracion. **No** se ocultan: se marcan, para que
 * se vea que existen y por que no se pueden jugar aun.
 */
export function isUnsupported(definition: CardDefinition | undefined): boolean {
  if (definition === undefined) {
    return true;
  }

  if (definition.type === 'spell') {
    return true;
  }

  if (definition.type === 'invocation') {
    return definition.effectText !== undefined || definition.penetration;
  }

  return definition.effectText !== undefined;
}

/** Convierte una carta de la mano propia en una fila pintable. */
export function toCardRow(card: CardInstance): CardRow {
  const definition = definitionOf(card);

  if (definition === undefined) {
    // No deberia ocurrir con el Primer Deck c5, pero un payload inesperado no
    // puede romper la pantalla.
    return {
      instanceId: card.instanceId,
      name: card.definitionId,
      type: 'Desconocida',
      costLabel: '—',
      stats: null,
      effectText: null,
      description: null,
      unsupported: true,
    };
  }

  const stats =
    definition.type === 'invocation'
      ? `P ${String(definition.power)} / V ${String(definition.life)} / A ${String(definition.range)}`
      : null;

  return {
    instanceId: card.instanceId,
    name: definition.name,
    type: TYPE_LABEL[definition.type],
    costLabel:
      definition.type === 'offer'
        ? `Valor ${String(definition.value)}`
        : `Coste ${String(definition.cost)}`,
    stats,
    effectText: definition.effectText ?? null,
    description: definition.description === '' ? null : definition.description,
    unsupported: isUnsupported(definition),
  };
}

function toOccupantRow(slot: FieldSlot): OccupantRow | null {
  if (slot === null) {
    return null;
  }

  const definition = definitionOf(slot.card);
  const name = definition?.name ?? slot.card.definitionId;

  if (slot.kind === 'spell') {
    return {
      instanceId: slot.card.instanceId,
      name,
      kind: 'spell',
      power: null,
      currentLife: null,
      maxLife: null,
      range: null,
      orientation: null,
    };
  }

  const printed = definition?.type === 'invocation' ? definition : undefined;

  return {
    instanceId: slot.card.instanceId,
    name,
    kind: 'invocation',
    power: printed?.power ?? null,
    currentLife: slot.currentLife,
    maxLife: printed?.life ?? null,
    range: printed?.range ?? null,
    orientation: slot.orientation,
  };
}

/** Las cinco posiciones de una linea, ocupadas o no. */
export function toLineRows(line: readonly FieldSlot[], lineId: FieldLineId): readonly SlotRow[] {
  return ([0, 1, 2, 3, 4] as const).map((index) => ({
    line: lineId,
    index,
    occupant: toOccupantRow(line[index] ?? null),
  }));
}

/** Las Ofrendas de un Altar, listas para pintar. */
export function toAltarRows(
  offers: MatchView['own']['board']['altar']['offers'],
): readonly AltarRow[] {
  return offers.map((offer) => {
    const definition = definitionOf(offer.card);

    return {
      instanceId: offer.card.instanceId,
      name: definition?.name ?? offer.card.definitionId,
      value: definition?.type === 'offer' ? definition.value : null,
      state: offer.orientation === 'faceUp' ? 'disponible' : 'agotada',
    };
  });
}

/** Cartas de un descarte, que es publico por ambos lados. */
export function toDiscardRows(discard: readonly CardInstance[]): readonly CardRow[] {
  return discard.map(toCardRow);
}

/** Cierto si le toca al espectador de esta vista. */
export function isMyTurn(view: MatchView): boolean {
  return view.turn.activeSeat === view.viewer;
}
